import type { ApiError } from '@/types/api';

// Same-origin fetch wrapper for the Laravel API. Sends the session cookie
// (credentials: 'include') and the X-XSRF-TOKEN header the web-group CSRF expects
// (ADR-0015). Non-2xx responses throw ApiRequestError carrying the localised message.
const BASE = '/api';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

function readXsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  // Don't set Content-Type for FormData — let the browser add multipart boundary.
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  const token = readXsrfToken();
  if (token) headers['X-XSRF-TOKEN'] = token;

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body !== undefined ? (isFormData ? (body as FormData) : JSON.stringify(body)) : undefined,
  });

  if (response.status === 204) return undefined as T;

  // Parse the body defensively. A misconfigured dev server (display_errors on) can
  // prepend a PHP warning to an otherwise-JSON response, and some error pages aren't
  // JSON at all — either way the wrapper must surface a clean Error, never let a raw
  // SyntaxError from JSON.parse reach the caller (the "Unexpected token '<'" crash).
  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  let data: unknown = null;
  if (isJson) {
    try {
      data = await response.json();
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const error = (data ?? {}) as ApiError;
    // 413 = the upload exceeded the server's post_max_size before any app validator ran,
    // so there's no field-level message and Laravel's stock 413 text is English. Surface
    // a localized size error matching the app's 20 MB contract (product-spec §Galleri).
    if (response.status === 413) {
      throw new ApiRequestError('Bildet er for stort. Maks 20 MB.', 413, error.errors);
    }
    throw new ApiRequestError(
      error.error ?? error.message ?? 'Noe gikk galt — prøv igjen om litt.',
      response.status,
      error.errors,
    );
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  // Multipart helper: pass FormData (with a `_method=PATCH` sentinel when needed —
  // browsers can't send PATCH multipart cleanly, so Laravel's Form Method Spoofing
  // turns a POST with _method into a PATCH).
  postForm: <T>(path: string, form: FormData) => request<T>('POST', path, form),
};
